/** @jsx isolateComponent */
import { css } from '@emotion/core';
import { isolateComponent } from '@zapier/style-encapsulation';
import { Colors, Link, Typography } from '@zapier/design-system';

type Props = {
  /**
   * The title of the section.
   */
  title: string;
  /**
   * The title of the section.
   */
  links: Array<{ text: string; href: string }>;
  /**
   * How many links to render intiially.
   */
  initiallyShownCount: number;
  /**
   * Indicates whether the details is open and additional links
   * have been revealed.
   */
  isExpanded?: boolean;
  /**
   * The handler for when the the additional links toggle is clicked.
   */
  onToggleExpanded?: React.MouseEventHandler;
};

// Tried to use `display: contents`, but it didn't seem
// to work when nested or when crossing into the `details`
// element. In fact `details` doesn't seem to support CSS Grid
// at all. I also tried using `inherit` on all of these values,
// but that didn't work. Needed to hardcode them and share
// them in JS, and then set a `margin` on the `summary`.
// Then I needed to do even more trickery, all because `details`
// doesn't support grid :(
const listGridGap = '1px';
const listGridStyles = css`
  display: grid;
  grid-template-columns: 100%;
  row-gap: ${listGridGap};
`;

// These are sibling elements, so no other way to share
// this code other than CSS variables. The toggle should
// be styled the same as the links.
const linkStyles = css`
  display: block;
  // Increase hit state
  padding: 3px 0;
  cursor: pointer;
  color: ${Colors.grey700};
  &:hover,
  &:focus {
    color: ${Colors.blue500};
  }
`;

// This is where things get _real_ tricky. I wanted this
// component to work without JS (so it works when it's just
// server-rendered HTML, which it will be in Django), so
// I made a toggle and a faux toggle, both of which are
// styled with these styles. The real toggle renders as the
// `summary`, and the faux toggle is a node nested further
// inside `details`, in a node that supports CSS Grid. When
// `details` is not `[open]`, the `summary` renders and behaves
// as usual. However when `details` _is_ `[open]`, the `summary`
// is visually hidden but positioned over the faux toggle, meaning
// it still functions. This allows the grid rows to be reversed
// so that the toggle always renders at the bottom of the list,
// and is the sole reason this code exists. As a bonus, in my
// testing this was very nicely accessible.
const toggleStyles = css`
  display: block;
  width: 100%;
  margin-bottom: ${listGridGap};
  ${linkStyles};
  text-decoration: underline;

  // Dynamically change content when the details
  // element is opened and closed.
  details:not([open]) &::before {
    content: 'Show more';
  }
  details[open] &::before {
    content: 'Show less';
  }
`;

const Styles = {
  root: css``,
  title: css`
    ${Typography.altText800};
    font-weight: 700;
    color: ${Colors.grey900};
    margin-bottom: 10px;
  `,
  links: css`
    ${Typography.base};
    font-size: 11px;
    line-height: 15px;
    ${listGridStyles};
    // Prevent users from accidentally selecting
    // text when interacting with details. I don't
    // see a need for them to be able to copy the
    // link text to their clipboards.
    user-select: none;
  `,
  linksList: css`
    ${listGridStyles};
  `,
  link: css`
    ${linkStyles};
  `,
  moreLinks: css`
    position: relative;
  `,
  moreLinksInner: css`
    ${listGridStyles};
  `,
  moreLinksToggle: css`
    // Hides default icon on details element
    &::-webkit-details-marker {
      display: none;
    }
    ${toggleStyles};

    // When details is open, the faux toggle will render
    // (so that it renders at the bottom of the list),
    // and the summary will be hidden but render over top
    // of it so it's still clickable.
    details[open] > & {
      position: absolute;
      bottom: 0;
      opacity: 0;
    }
  `,
  moreLinksFauxToggle: css`
    ${toggleStyles};
  `,
};

const LinksList = (props: Pick<Props, 'links'>) => {
  const linkNodes = props.links.map((link) => {
    return (
      <li key={link.text}>
        <Link css={Styles.link} color={null} href={link.href}>
          {link.text}
        </Link>
      </li>
    );
  });
  return <ul css={Styles.linksList}>{linkNodes}</ul>;
};

const MoreLinks = (
  props: Pick<Props, 'links' | 'isExpanded' | 'onToggleExpanded'>
) => {
  return (
    <details css={Styles.moreLinks} open={props.isExpanded}>
      <summary
        aria-label="Toggle more links"
        css={Styles.moreLinksToggle}
        onClick={props.onToggleExpanded}
      >
        {/* See styles for content. */}
      </summary>
      <div css={Styles.moreLinksInner}>
        <LinksList links={props.links} />
        <span aria-hidden={true} css={Styles.moreLinksFauxToggle}>
          {/* See styles for content. */}
        </span>
      </div>
    </details>
  );
};

/**
 * @private
 * An expandable list of links with a title.
 */
export const LinksSection = (props: Props) => {
  const { initiallyShownCount, links } = props;
  const moreLinks = links.length > initiallyShownCount && (
    <MoreLinks
      links={links.slice(initiallyShownCount)}
      isExpanded={props.isExpanded}
      onToggleExpanded={props.onToggleExpanded}
    />
  );
  return (
    <section css={Styles.root}>
      <h5 css={Styles.title}>{props.title}</h5>
      <div css={Styles.links}>
        <LinksList links={links.slice(0, initiallyShownCount)} />
        {moreLinks}
      </div>
    </section>
  );
};

LinksSection.defaultProps = {
  initiallyShownCount: 5,
};
